interface PluralForms {
  one: string; // 1, 21, 31... (1 выбран)
  few: string; // 2-4, 22-24... (2 выбрано)
  many: string; // 0, 5-20, 25-30... (5 выбрано)
}

interface PluralConfig {
  [key: string]: PluralForms;
}

function getPluralForm(count: number): "one" | "few" | "many" {
  const absCount = Math.abs(count);
  const lastDigit = absCount % 10;
  const lastTwoDigits = absCount % 100;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
    return "many";
  }

  if (lastDigit === 1) {
    return "one";
  }

  if (lastDigit >= 2 && lastDigit <= 4) {
    return "few";
  }

  return "many";
}

function pluralize(count: number, forms: PluralForms): string;
function pluralize(count: number, key: string, config: PluralConfig): string;
function pluralize(
  count: number,
  formsOrKey: PluralForms | string,
  config?: PluralConfig
): string {
  if (typeof formsOrKey === "string" && config) {
    const forms = config[formsOrKey];
    if (!forms) {
      console.warn(`Plural forms not found for key: ${formsOrKey}`);
      return formsOrKey;
    }
    return pluralize(count, forms);
  } else if (typeof formsOrKey === "object") {
    const form = getPluralForm(count);
    return formsOrKey[form];
  }
  return "";
}

const ruPluralConfig: PluralConfig = {
  selected: {
    one: "выбран",
    few: "выбрано",
    many: "выбрано",
  },
  user: {
    one: "пользователь",
    few: "пользователя",
    many: "пользователей",
  },
  ad: {
    one: "объявление",
    few: "объявления",
    many: "объявлений",
  },
  topic: {
    one: "тема",
    few: "темы",
    many: "тем",
  },
  section: {
    one: "раздел",
    few: "раздела",
    many: "разделов",
  },
  subsection: {
    one: "подраздел",
    few: "подраздела",
    many: "подразделов",
  },
};

export const locale = {
  selected: (count: number) => pluralize(count, ruPluralConfig.selected),
  users: (count: number) => pluralize(count, ruPluralConfig.user),
  ads: (count: number) => pluralize(count, ruPluralConfig.ad),
  topics: (count: number) => pluralize(count, ruPluralConfig.topic),
  sections: (count: number) => pluralize(count, ruPluralConfig.section),
  subsections: (count: number) => pluralize(count, ruPluralConfig.subsection),

  withCount: {
    selected: (count: number) => `${count} ${locale.selected(count)}`,
    users: (count: number) => `${count} ${locale.users(count)}`,
    ads: (count: number) => `${count} ${locale.ads(count)}`,
    topics: (count: number) => `${count} ${locale.topics(count)}`,
    sections: (count: number) => `${count} ${locale.sections(count)}`,
    subsections: (count: number) => `${count} ${locale.subsections(count)}`,
  },
};

export const truncateText = (
  text: string | null | undefined,
  maxLength: number = 32,
  suffix: string = "..."
): string => {
  if (!text) return "";
  if (text.length <= maxLength) return text;

  return text.slice(0, maxLength) + suffix;
};

export const convertToSnakeCase = (str: string): string => {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
};

export function renderTruncatedText<V = string>(
  value: V,
  max: number,
  mapFunction?: (val: V) => string,
  renderFunction?: (text: string, shouldTruncate: boolean, truncatedText: string) => React.ReactNode
): React.ReactNode {
  if (value == null) return null;

  const text = mapFunction ? mapFunction(value) : String(value);
  const shouldTruncate = Boolean(text && text.length > max);
  const truncatedText = shouldTruncate ? truncateText(text, max) : text;

  if (renderFunction) {
    return renderFunction(text, shouldTruncate, truncatedText);
  }

  return (
    <span title={shouldTruncate ? text : undefined}>
      {truncatedText}
    </span>
  );
}